import { useState, useEffect, useRef, useCallback } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Generate / restore a stable session id per device so chat persists across page refreshes.
const getSessionId = () => {
  let sid = localStorage.getItem("mf_ai_session_id");
  if (!sid) {
    sid = `mf-ai-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem("mf_ai_session_id", sid);
  }
  return sid;
};

const QUICK_PROMPTS = [
  "What palette pairs with darkened oak floors?",
  "What's the material cap for a 2BR?",
  "Give me one line to use with the owner about why the Stabilization Fee is locked.",
  "EPA RRP — does this 1985 unit need a certified renovator?",
  "Is this rent worth a portfolio discount, or hold standard?",
  "Draft a 2-sentence follow-up for a property manager who ghosted me.",
];

// One-tap "Starter Packs" — tap a card, AI drafts something useful instantly. No typing.
const STARTER_PACKS = [
  { id: "owner-email-tax", icon: "💰", label: "Tax Talk Email", prompt: "Draft a 2-sentence email to a DFW property owner explaining how working with Moovin Forward saves them on Schedule C taxes (Activation Capital and Stabilization Fee both deductible). Warm, direct, no jargon." },
  { id: "walkthrough-line", icon: "🔨", label: "Walkthrough One-Liner", prompt: "Give me ONE confident sentence I can say to a property owner during a walkthrough about why our Stabilization Fee is locked at the rent multiplier and not negotiable." },
  { id: "owner-email-pitch", icon: "📧", label: "First-Touch Owner Email", prompt: "Draft a warm, professional 3-sentence first-touch email to a DFW property owner introducing Moovin Forward as their make-ready and asset stabilization partner. End with a soft ask for a 15-minute walkthrough." },
  { id: "cold-call", icon: "📞", label: "Cold Call Opener", prompt: "Give me a 30-second cold call opener for a property manager who has 20+ vacant units. Lead with vacancy cost, end with the ask for a free walkthrough on one unit." },
  { id: "pitch-pm", icon: "🚪", label: "Property Manager Pitch", prompt: "Draft a confident 4-sentence pitch I can use when I walk into a DFW property management office. Lead with the 50/50 model and zero PO risk. End with the ask for a pilot unit." },
  { id: "scope-approval", icon: "📋", label: "Scope Approval Reminder", prompt: "Draft a friendly 2-sentence reminder email to a client who hasn't approved the scope yet. Make it warm, no pressure, just a gentle nudge with a deadline." },
  { id: "ghost-followup", icon: "👻", label: "Ghosted Lead Follow-up", prompt: "Draft a 2-sentence follow-up email to a property owner who hasn't responded in 7 days. Warm, not desperate. Reference the vacancy clock running." },
  { id: "voucher-pitch", icon: "🏛️", label: "Voucher Coalition Outreach", prompt: "Draft a 3-sentence intro email to a DFW housing voucher coordinator (HUD-VASH or similar) introducing Moovin Forward as a NSPIRE-compliant turnover partner for their voucher portfolio." },
];

const MoovinAiAssistant = () => {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(getSessionId);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [error, setError] = useState("");

  // Save-script modal state
  const [saveModal, setSaveModal] = useState(null); // { content }
  const [saveTitle, setSaveTitle] = useState("");
  const [saveTags, setSaveTags] = useState("");
  const [saveContent, setSaveContent] = useState("");
  const [saving, setSaving] = useState(false);
  const [savedToast, setSavedToast] = useState("");
  const [lastSaved, setLastSaved] = useState(null);  // For toast → edit shortcut

  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Load history when widget opens
  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/ai/history?session_id=${encodeURIComponent(sessionId)}`, {
        credentials: "include",
      });
      if (r.ok) {
        const d = await r.json();
        setMessages(d.messages || []);
      }
    } catch {}
    setLoadingHistory(false);
  }, [sessionId]);

  useEffect(() => {
    if (open) {
      loadHistory();
      // Auto-focus after a tick so the panel finishes mounting
      setTimeout(() => inputRef.current?.focus(), 120);
    }
  }, [open, loadHistory]);

  // Auto-scroll to bottom on new message
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const send = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || sending) return;
    setError("");
    setSending(true);
    setInput("");
    // Optimistic user bubble
    const optimistic = [...messages, { role: "user", content: text }];
    setMessages(optimistic);
    try {
      const r = await fetch(`${BACKEND_URL}/api/ai/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || `Request failed (${r.status})`);
      }
      const d = await r.json();
      setMessages([...optimistic, { role: "assistant", content: d.reply }]);
    } catch (e) {
      setError(e.message || "Couldn't reach Moovin AI right now.");
      setMessages(optimistic); // keep the user message visible
    }
    setSending(false);
    setTimeout(() => inputRef.current?.focus(), 80);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const newConversation = () => {
    if (sending) return;
    if (!confirm("Start a fresh conversation? This won't delete the old one — it just opens a new thread.")) return;
    const fresh = `mf-ai-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem("mf_ai_session_id", fresh);
    setSessionId(fresh);
    setMessages([]);
  };

  const openSaveModal = (content) => {
    // Auto-suggest a title from the first line / first 60 chars
    const firstLine = content.split("\n").find(l => l.trim().length > 8) || content;
    const suggested = firstLine.replace(/^[#\-\*\s]+/, "").trim().slice(0, 80);
    setSaveTitle(suggested);
    setSaveContent(content);
    setSaveTags("");
    setSaveModal({ content });
  };

  // ONE-TAP quick save — AI auto-generates title + tags, no modal. Toast confirms with edit option.
  const quickSave = async (content) => {
    if (!content || !content.trim()) return;
    setSavedToast("Saving…");
    try {
      const r = await fetch(`${BACKEND_URL}/api/ai/scripts/quick-save`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content, source_session_id: sessionId }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || "Save failed");
      }
      const saved = await r.json();
      const tagStr = (saved.tags || []).join(", ") || "no tags";
      setSavedToast(`✓ Saved as "${saved.title}" — tagged ${tagStr}. Tap to rename.`);
      // Stash the saved script so tapping the toast opens the edit modal
      setLastSaved(saved);
      setTimeout(() => setSavedToast(""), 5000);
    } catch (e) {
      setSavedToast("");
      setError(e.message || "Could not save script.");
    }
  };

  // Quick "follow-up" actions on an AI reply — one-tap rewrites without typing
  const askFollowUp = (originalContent, instruction) => {
    if (sending) return;
    const prompt = `${instruction}\n\nOriginal:\n${originalContent}`;
    send(prompt);
  };

  const saveScript = async () => {
    if (!saveTitle.trim() || !saveContent.trim()) return;
    setSaving(true);
    try {
      const tags = saveTags.split(",").map(t => t.trim()).filter(Boolean);
      const isRename = saveModal && saveModal.isRename && saveModal.scriptId;
      const url = isRename
        ? `${BACKEND_URL}/api/ai/scripts/${saveModal.scriptId}`
        : `${BACKEND_URL}/api/ai/scripts`;
      const method = isRename ? "PUT" : "POST";
      const r = await fetch(url, {
        method,
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: saveTitle.trim(),
          content: saveContent,
          tags,
          ...(isRename ? {} : { source_session_id: sessionId }),
        }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || "Save failed");
      }
      setSavedToast(isRename ? "Script updated ✓" : "Saved to your Scripts Library ✓");
      setSaveModal(null);
      setLastSaved(null);
      setTimeout(() => setSavedToast(""), 2500);
    } catch (e) {
      setError(e.message || "Could not save script.");
    }
    setSaving(false);
  };

  return (
    <>
      {/* Floating launcher — always visible, bottom-right */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="moovin-ai-fab"
          aria-label="Ask Moovin AI"
          style={{
            position: "fixed",
            bottom: 90,
            right: 24,
            zIndex: 9998,
            width: 60,
            height: 60,
            borderRadius: "50%",
            background: "linear-gradient(135deg, #00DC78 0%, #00FFFF 100%)",
            border: "none",
            color: "#030B1A",
            fontSize: "1.5rem",
            fontWeight: 900,
            cursor: "pointer",
            boxShadow: "0 6px 24px rgba(0,220,120,0.45), 0 2px 8px rgba(0,0,0,0.3)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          title="Ask Moovin AI — your in-app advisor"
        >
          <span style={{ fontSize: "1.6rem", lineHeight: 1 }}>✦</span>
        </button>
      )}

      {/* Slide-out panel */}
      {open && (
        <div
          data-testid="moovin-ai-panel"
          style={{
            position: "fixed",
            bottom: 90,
            right: 24,
            zIndex: 9999,
            width: "min(420px, calc(100vw - 32px))",
            height: "min(640px, calc(100vh - 48px))",
            background: "#030B1A",
            border: "1px solid rgba(0,255,255,0.25)",
            borderRadius: 14,
            boxShadow: "0 20px 60px rgba(0,0,0,0.55), 0 0 40px rgba(0,255,255,0.08)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <div style={{
            padding: "0.95rem 1.1rem",
            background: "linear-gradient(135deg, rgba(0,220,120,0.10) 0%, rgba(0,255,255,0.06) 100%)",
            borderBottom: "1px solid rgba(255,255,255,0.06)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <span style={{ color: "#00FFFF", fontSize: "1.1rem" }}>✦</span>
                <h3 style={{ color: "#fff", fontSize: "1rem", fontWeight: 800, margin: 0, letterSpacing: "-0.01em" }}>Moovin AI</h3>
                <span style={{ background: "rgba(0,220,120,0.15)", color: "#00DC78", fontSize: "0.62rem", fontWeight: 800, letterSpacing: "0.10em", textTransform: "uppercase", padding: "0.15rem 0.45rem", borderRadius: 4 }}>Claude · Internal</span>
              </div>
              <div style={{ color: "#7E92A8", fontSize: "0.7rem", marginTop: "0.18rem" }}>Your field advisor — never leaves the app.</div>
            </div>
            <div style={{ display: "flex", gap: "0.35rem" }}>
              <button onClick={newConversation} data-testid="moovin-ai-new" title="New conversation" style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.15)", color: "#ADBED1", padding: "0.35rem 0.55rem", borderRadius: 5, fontSize: "0.7rem", cursor: "pointer", fontWeight: 700 }}>+ New</button>
              <button onClick={() => setOpen(false)} data-testid="moovin-ai-close" aria-label="Close" style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.15)", color: "#ADBED1", padding: "0.35rem 0.55rem", borderRadius: 5, fontSize: "0.85rem", cursor: "pointer", lineHeight: 1 }}>×</button>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} data-testid="moovin-ai-messages" style={{ flex: 1, overflowY: "auto", padding: "1rem 1.1rem", display: "flex", flexDirection: "column", gap: "0.6rem" }}>
            {loadingHistory && messages.length === 0 ? (
              <div style={{ color: "#7E92A8", textAlign: "center", padding: "1rem", fontSize: "0.82rem" }}>Loading conversation…</div>
            ) : messages.length === 0 ? (
              <div data-testid="moovin-ai-empty" style={{ color: "#ADBED1", fontSize: "0.85rem", lineHeight: 1.55, padding: "0.5rem 0" }}>
                <div style={{ color: "#fff", fontWeight: 700, marginBottom: "0.4rem" }}>Hey Shannon. Tap one — I'll draft it.</div>
                <div style={{ color: "#7E92A8", fontSize: "0.78rem", marginBottom: "0.85rem" }}>No typing. Just tap. I know your caps, palettes, brand voice, and TREC rules.</div>

                {/* STARTER PACKS — one-tap cards (this is what Shannon asked for) */}
                <div style={{ color: "#7E92A8", fontSize: "0.66rem", fontWeight: 700, letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: "0.5rem" }}>✨ Starter Packs</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "0.4rem", marginBottom: "1rem" }}>
                  {STARTER_PACKS.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => send(p.prompt)}
                      data-testid={`starter-pack-${p.id}`}
                      style={{
                        textAlign: "left",
                        background: "linear-gradient(135deg, rgba(0,220,120,0.06) 0%, rgba(0,255,255,0.04) 100%)",
                        border: "1px solid rgba(0,255,255,0.18)",
                        color: "#fff",
                        padding: "0.6rem 0.7rem",
                        borderRadius: 7,
                        fontSize: "0.74rem",
                        cursor: "pointer",
                        lineHeight: 1.3,
                        fontWeight: 600,
                      }}
                    >
                      <div style={{ fontSize: "1.1rem", marginBottom: "0.18rem" }}>{p.icon}</div>
                      <div style={{ color: "#fff" }}>{p.label}</div>
                    </button>
                  ))}
                </div>

                <div style={{ color: "#7E92A8", fontSize: "0.66rem", fontWeight: 700, letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: "0.4rem" }}>Or ask me anything</div>
                <div style={{ display: "flex", flexDirection: "column", gap: "0.35rem" }}>
                  {QUICK_PROMPTS.map((q, i) => (
                    <button key={i} onClick={() => send(q)} data-testid={`moovin-ai-quick-${i}`} style={{ textAlign: "left", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", color: "#ADBED1", padding: "0.5rem 0.7rem", borderRadius: 6, fontSize: "0.76rem", cursor: "pointer", lineHeight: 1.4 }}>
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((m, i) => (
                <Bubble
                  key={i}
                  role={m.role}
                  content={m.content}
                  isLast={i === messages.length - 1}
                  onQuickSave={m.role === "assistant" && m.content ? () => quickSave(m.content) : null}
                  onFollowUp={m.role === "assistant" && m.content ? (instr) => askFollowUp(m.content, instr) : null}
                />
              ))
            )}
            {sending && <Bubble role="assistant" content="…" pending />}
          </div>

          {/* Error strip */}
          {error && (
            <div style={{ background: "rgba(255,139,107,0.10)", borderTop: "1px solid rgba(255,139,107,0.30)", color: "#FF8B6B", padding: "0.5rem 1.1rem", fontSize: "0.76rem" }}>
              {error}
            </div>
          )}

          {/* Composer */}
          <div style={{ padding: "0.7rem 0.85rem", borderTop: "1px solid rgba(255,255,255,0.06)", background: "rgba(0,0,0,0.25)" }}>
            <div style={{ display: "flex", gap: "0.5rem", alignItems: "flex-end" }}>
              <textarea
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Ask anything — palette, math, pitch, EPA, owner phrasing…"
                data-testid="moovin-ai-input"
                rows={2}
                disabled={sending}
                style={{
                  flex: 1,
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.12)",
                  color: "#fff",
                  padding: "0.55rem 0.7rem",
                  borderRadius: 6,
                  fontSize: "0.85rem",
                  resize: "none",
                  fontFamily: "inherit",
                  lineHeight: 1.4,
                }}
              />
              <button
                onClick={() => send()}
                disabled={sending || !input.trim()}
                data-testid="moovin-ai-send"
                style={{
                  background: sending || !input.trim() ? "rgba(255,255,255,0.08)" : "linear-gradient(135deg, #00DC78 0%, #00FFFF 100%)",
                  border: "none",
                  color: "#030B1A",
                  padding: "0.55rem 0.95rem",
                  borderRadius: 6,
                  fontWeight: 800,
                  fontSize: "0.82rem",
                  cursor: sending || !input.trim() ? "not-allowed" : "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                {sending ? "…" : "Send"}
              </button>
            </div>
            <div style={{ color: "#7E92A8", fontSize: "0.66rem", marginTop: "0.35rem", textAlign: "right" }}>
              Enter to send · Shift+Enter for newline · Internal-only
            </div>
          </div>
        </div>
      )}

      {/* Save-to-Scripts modal */}
      {saveModal && (
        <div
          data-testid="moovin-ai-save-modal"
          onClick={() => !saving && setSaveModal(null)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 10000,
            background: "rgba(0,0,0,0.75)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "1rem",
          }}
        >
          <div onClick={e => e.stopPropagation()} style={{
            width: "min(560px, 100%)",
            background: "#030B1A",
            border: "1px solid rgba(0,255,255,0.30)",
            borderRadius: 12,
            padding: "1.4rem 1.5rem",
            boxShadow: "0 24px 70px rgba(0,0,0,0.6)",
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
              <h3 style={{ color: "#fff", fontSize: "1.05rem", fontWeight: 800, margin: 0 }}>💾 Save to Scripts Library</h3>
              <button onClick={() => !saving && setSaveModal(null)} style={{ background: "transparent", border: "none", color: "#ADBED1", fontSize: "1.2rem", cursor: "pointer" }}>×</button>
            </div>

            <label style={{ color: "#7E92A8", fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.10em", textTransform: "uppercase", display: "block", marginBottom: "0.3rem" }}>Title</label>
            <input
              value={saveTitle}
              onChange={e => setSaveTitle(e.target.value)}
              data-testid="save-modal-title"
              placeholder="e.g. Tax Savings — Schedule C explainer"
              style={{ width: "100%", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.15)", color: "#fff", padding: "0.55rem 0.7rem", borderRadius: 6, fontSize: "0.88rem", marginBottom: "0.85rem" }}
            />

            <label style={{ color: "#7E92A8", fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.10em", textTransform: "uppercase", display: "block", marginBottom: "0.3rem" }}>Tags <span style={{ textTransform: "none", letterSpacing: 0, color: "#5A7090" }}>(comma-separated)</span></label>
            <input
              value={saveTags}
              onChange={e => setSaveTags(e.target.value)}
              data-testid="save-modal-tags"
              placeholder="Tax, Owner Email, Walkthrough, Pitch, EPA…"
              style={{ width: "100%", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.15)", color: "#fff", padding: "0.55rem 0.7rem", borderRadius: 6, fontSize: "0.85rem", marginBottom: "0.85rem" }}
            />

            <label style={{ color: "#7E92A8", fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.10em", textTransform: "uppercase", display: "block", marginBottom: "0.3rem" }}>Content <span style={{ textTransform: "none", letterSpacing: 0, color: "#5A7090" }}>(edit before saving if you want)</span></label>
            <textarea
              value={saveContent}
              onChange={e => setSaveContent(e.target.value)}
              rows={8}
              data-testid="save-modal-content"
              style={{ width: "100%", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.15)", color: "#fff", padding: "0.55rem 0.7rem", borderRadius: 6, fontSize: "0.84rem", lineHeight: 1.5, fontFamily: "inherit", resize: "vertical", marginBottom: "1rem" }}
            />

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
              <button onClick={() => !saving && setSaveModal(null)} disabled={saving} style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.15)", color: "#ADBED1", padding: "0.55rem 1rem", borderRadius: 6, fontSize: "0.82rem", cursor: saving ? "wait" : "pointer", fontWeight: 700 }}>Cancel</button>
              <button
                onClick={saveScript}
                disabled={saving || !saveTitle.trim() || !saveContent.trim()}
                data-testid="save-modal-confirm"
                style={{
                  background: saving || !saveTitle.trim() || !saveContent.trim() ? "rgba(255,255,255,0.08)" : "linear-gradient(135deg, #00DC78 0%, #00FFFF 100%)",
                  border: "none",
                  color: "#030B1A",
                  padding: "0.55rem 1.1rem",
                  borderRadius: 6,
                  fontWeight: 800,
                  fontSize: "0.85rem",
                  cursor: saving || !saveTitle.trim() || !saveContent.trim() ? "not-allowed" : "pointer",
                }}
              >
                {saving ? "Saving…" : "💾 Save Script"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast — tap to rename the just-saved script */}
      {savedToast && (
        <div
          data-testid="moovin-ai-saved-toast"
          onClick={() => {
            if (lastSaved) {
              setSaveTitle(lastSaved.title || "");
              setSaveTags((lastSaved.tags || []).join(", "));
              setSaveContent(lastSaved.content || "");
              setSaveModal({ content: lastSaved.content, scriptId: lastSaved.script_id, isRename: true });
              setSavedToast("");
            }
          }}
          style={{
            position: "fixed",
            bottom: 100,
            right: 24,
            zIndex: 10001,
            background: "linear-gradient(135deg, #00DC78 0%, #00FFFF 100%)",
            color: "#030B1A",
            padding: "0.65rem 1.1rem",
            borderRadius: 8,
            fontWeight: 800,
            fontSize: "0.78rem",
            boxShadow: "0 6px 20px rgba(0,220,120,0.4)",
            cursor: lastSaved ? "pointer" : "default",
            maxWidth: 360,
            lineHeight: 1.4,
          }}
        >
          {savedToast}
        </div>
      )}
    </>
  );
};

// ─── Bubble — assistant replies get one-tap save + follow-up rewrite buttons ───
const Bubble = ({ role, content, pending = false, isLast = false, onQuickSave = null, onFollowUp = null }) => {
  const isUser = role === "user";
  const [copied, setCopied] = useState(false);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {}
  };

  return (
    <div data-testid={`moovin-ai-bubble-${role}`} style={{ display: "flex", flexDirection: "column", alignItems: isUser ? "flex-end" : "flex-start" }}>
      <div style={{
        maxWidth: "82%",
        background: isUser ? "linear-gradient(135deg, rgba(0,220,120,0.18) 0%, rgba(0,255,255,0.10) 100%)" : "rgba(255,255,255,0.04)",
        border: `1px solid ${isUser ? "rgba(0,255,255,0.25)" : "rgba(255,255,255,0.08)"}`,
        color: pending ? "#7E92A8" : "#fff",
        padding: "0.6rem 0.85rem",
        borderRadius: 10,
        fontSize: "0.86rem",
        lineHeight: 1.55,
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        fontStyle: pending ? "italic" : "normal",
      }}>
        {content}
      </div>

      {/* Action row: one-tap save + copy on every assistant bubble */}
      {!pending && !isUser && (
        <div style={{ display: "flex", gap: "0.4rem", marginTop: "0.3rem", flexWrap: "wrap" }}>
          {onQuickSave && (
            <button
              onClick={onQuickSave}
              data-testid="moovin-ai-bubble-quicksave"
              style={{ background: "linear-gradient(135deg, rgba(0,220,120,0.20) 0%, rgba(0,255,255,0.12) 100%)", border: "1px solid rgba(0,220,120,0.30)", color: "#00DC78", fontSize: "0.7rem", cursor: "pointer", padding: "0.25rem 0.6rem", fontWeight: 700, borderRadius: 5 }}
              title="One-tap save — auto title + tags"
            >
              💾 Save
            </button>
          )}
          <button
            onClick={copyToClipboard}
            data-testid="moovin-ai-bubble-copy"
            style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.12)", color: "#ADBED1", fontSize: "0.7rem", cursor: "pointer", padding: "0.25rem 0.6rem", fontWeight: 600, borderRadius: 5 }}
            title="Copy to clipboard"
          >
            {copied ? "✓ Copied" : "📋 Copy"}
          </button>
        </div>
      )}

      {/* Suggested follow-ups — only on the LAST assistant message to avoid clutter */}
      {!pending && !isUser && isLast && onFollowUp && (
        <div style={{ display: "flex", gap: "0.35rem", marginTop: "0.4rem", flexWrap: "wrap" }}>
          <span style={{ color: "#7E92A8", fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", alignSelf: "center" }}>↳ Try</span>
          <button
            onClick={() => onFollowUp("Make this shorter — half the length. Same point.")}
            data-testid="moovin-ai-followup-shorter"
            style={{ background: "rgba(0,255,255,0.05)", border: "1px solid rgba(0,255,255,0.20)", color: "#5AC8D8", fontSize: "0.68rem", cursor: "pointer", padding: "0.22rem 0.55rem", fontWeight: 700, borderRadius: 4 }}
          >
            🔁 Shorter
          </button>
          <button
            onClick={() => onFollowUp("Rewrite this for the property owner — warmer, simpler language, no internal jargon. Keep TREC-safe.")}
            data-testid="moovin-ai-followup-owner"
            style={{ background: "rgba(0,255,255,0.05)", border: "1px solid rgba(0,255,255,0.20)", color: "#5AC8D8", fontSize: "0.68rem", cursor: "pointer", padding: "0.22rem 0.55rem", fontWeight: 700, borderRadius: 4 }}
          >
            👤 Owner-friendly
          </button>
          <button
            onClick={() => onFollowUp("Make this more confident and decisive. Less hedging.")}
            data-testid="moovin-ai-followup-confident"
            style={{ background: "rgba(0,255,255,0.05)", border: "1px solid rgba(0,255,255,0.20)", color: "#5AC8D8", fontSize: "0.68rem", cursor: "pointer", padding: "0.22rem 0.55rem", fontWeight: 700, borderRadius: 4 }}
          >
            🌟 More confident
          </button>
        </div>
      )}
    </div>
  );
};

export default MoovinAiAssistant;
